import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CalendarX, Check, Clock, FileCheck, LayoutGrid, List, UserX } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { Button } from '../ui';
import AttendanceGrid from './AttendanceGrid';
import type { AttendanceStatus, KlassenbuchParticipant } from '../../lib/klassenbuchApi';
import { loadStudentPhotos } from '../../lib/photoUtils';

type Props = {
  participants: KlassenbuchParticipant[];
  disabled?: boolean;
  onSet: (studentKey: string, status: AttendanceStatus) => void;
  onOpenExcuse?: (participant: KlassenbuchParticipant) => void;
  parentType: 'class' | 'course' | 'group' | 'ad_hoc';
  parentId: number | null;
  className?: string | null;
  groupName?: string | null;
};

type AttendanceMode = 'list' | 'seatplan';

type SeatplanSummary = {
  id: number;
  name: string;
  klasse?: string | null;
  context_type?: 'class' | 'course' | 'group' | null;
  context_value?: string | null;
  context_label?: string | null;
};

type SeatplanPosition = {
  grid_row: number;
  grid_col: number;
  schueler_id: number;
  sophomorix_unid?: string | null;
  vorname: string;
  name: string;
  klasse: string;
};

type SeatplanDetail = {
  id: number;
  name: string;
  grid_rows: number;
  grid_cols: number;
  positionen: SeatplanPosition[];
};

const STATUS_ACTIONS: Array<{
  status: AttendanceStatus;
  label: string;
  title: string;
  icon: ReactNode;
  activeClass: string;
}> = [
  {
    status: 'present',
    label: 'A',
    title: 'Anwesend',
    icon: <Check size={13} />,
    activeClass:
      'bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-950/60 dark:border-emerald-800 dark:text-emerald-200'
  },
  {
    status: 'late',
    label: 'V',
    title: 'Verspätet',
    icon: <Clock size={13} />,
    activeClass:
      'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-950/60 dark:border-amber-800 dark:text-amber-200'
  },
  {
    status: 'excused',
    label: 'E',
    title: 'Entschuldigt',
    icon: <FileCheck size={13} />,
    activeClass:
      'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-950/60 dark:border-blue-800 dark:text-blue-200'
  },
  {
    status: 'beurlaubt',
    label: 'B',
    title: 'Beurlaubt',
    icon: <CalendarX size={13} />,
    activeClass:
      'bg-purple-100 border-purple-300 text-purple-800 dark:bg-purple-950/60 dark:border-purple-800 dark:text-purple-200'
  },
  {
    status: 'absent',
    label: 'F',
    title: 'Fehlt (unentschuldigt)',
    icon: <UserX size={13} />,
    activeClass:
      'bg-rose-100 border-rose-300 text-rose-800 dark:bg-rose-950/60 dark:border-rose-800 dark:text-rose-200'
  }
];

const STATUS_DOT_CLASS: Record<AttendanceStatus, string> = {
  present: 'bg-emerald-500',
  late: 'bg-amber-500',
  excused: 'bg-blue-500',
  beurlaubt: 'bg-purple-500',
  absent: 'bg-rose-500'
};

function getStudentKey(p: KlassenbuchParticipant): string {
  return p.sophomorixUnid || p.Student_ID || p.username;
}

function normalizeName(value: string | null | undefined): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function matchesPlanContext(
  plan: SeatplanSummary,
  parentType: Props['parentType'],
  parentId: number | null,
  className?: string | null,
  groupName?: string | null
): boolean {
  if (parentType === 'class') {
    const byLegacyClass = className ? normalizeName(plan.klasse) === normalizeName(className) : false;
    const byContextType =
      plan.context_type === 'class' &&
      className &&
      normalizeName(plan.context_value) === normalizeName(className);
    const byContextId =
      plan.context_type === 'class' &&
      parentId != null &&
      String(plan.context_value || '').trim() === String(parentId);
    return byLegacyClass || byContextType || byContextId;
  }
  if (parentType === 'group') {
    const byContextName =
      plan.context_type === 'group' &&
      groupName &&
      (normalizeName(plan.context_value) === normalizeName(groupName) ||
        normalizeName(plan.context_label) === normalizeName(groupName));
    const byContextId =
      plan.context_type === 'group' &&
      parentId != null &&
      String(plan.context_value || '').trim() === String(parentId);
    return byContextName || byContextId;
  }
  return false;
}

export default function AttendanceSeatplan({
  participants,
  disabled,
  onSet,
  onOpenExcuse,
  parentType,
  parentId,
  className,
  groupName
}: Props) {
  const modeKey = `klassenbuch.attendance.mode.${parentType}.${parentId ?? 'none'}`;
  const seatplanKey = `klassenbuch.attendance.seatplan.${parentType}.${parentId ?? 'none'}`;
  const [mode, setMode] = useState<AttendanceMode>(() => {
    const raw = localStorage.getItem(modeKey);
    return raw === 'seatplan' ? 'seatplan' : 'list';
  });
  const [seatplans, setSeatplans] = useState<SeatplanSummary[]>([]);
  const [selectedSeatplanId, setSelectedSeatplanId] = useState<number | null>(null);
  const [seatplanDetail, setSeatplanDetail] = useState<SeatplanDetail | null>(null);
  const [seatplanPhotos, setSeatplanPhotos] = useState<Record<string, string | null>>({});
  const [loadingSeatplans, setLoadingSeatplans] = useState(false);

  useEffect(() => {
    localStorage.setItem(modeKey, mode);
  }, [modeKey, mode]);

  useEffect(() => {
    if (!['class', 'group'].includes(parentType) || !parentId) {
      setSeatplans([]);
      setSelectedSeatplanId(null);
      setSeatplanDetail(null);
      return;
    }
    let cancelled = false;
    const loadSeatplans = async () => {
      setLoadingSeatplans(true);
      try {
        const res = await apiClient.get('/sitzplaene?overview=true');
        const allPlans = Array.isArray(res?.data?.data) ? (res.data.data as SeatplanSummary[]) : [];
        const filtered = allPlans.filter((plan) =>
          matchesPlanContext(plan, parentType, parentId, className, groupName)
        );
        if (cancelled) return;
        setSeatplans(filtered);
        const preferredRaw = localStorage.getItem(seatplanKey);
        const preferredId = preferredRaw ? Number(preferredRaw) : null;
        const initial =
          filtered.find((plan) => Number(plan.id) === preferredId)?.id ?? filtered[0]?.id ?? null;
        setSelectedSeatplanId(initial);
      } catch {
        if (!cancelled) {
          setSeatplans([]);
          setSelectedSeatplanId(null);
        }
      } finally {
        if (!cancelled) setLoadingSeatplans(false);
      }
    };
    void loadSeatplans();
    return () => {
      cancelled = true;
    };
  }, [parentType, parentId, className, groupName, seatplanKey]);

  useEffect(() => {
    if (!selectedSeatplanId) {
      setSeatplanDetail(null);
      setSeatplanPhotos({});
      return;
    }
    localStorage.setItem(seatplanKey, String(selectedSeatplanId));
    let cancelled = false;
    const loadDetail = async () => {
      try {
        const res = await apiClient.get(`/sitzplaene/${selectedSeatplanId}`);
        if (!cancelled) {
          setSeatplanDetail((res?.data?.data || null) as SeatplanDetail | null);
        }
      } catch {
        if (!cancelled) setSeatplanDetail(null);
      }
    };
    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedSeatplanId, seatplanKey]);

  useEffect(() => {
    const ids = Array.from(
      new Set(
        (seatplanDetail?.positionen || [])
          .map((pos) => String(pos.sophomorix_unid || '').trim())
          .filter((id) => !!id)
      )
    );
    if (!ids.length) {
      setSeatplanPhotos({});
      return;
    }
    let cancelled = false;
    const loadPhotos = async () => {
      const photos = await loadStudentPhotos(ids);
      if (!cancelled) setSeatplanPhotos(photos);
    };
    void loadPhotos();
    return () => {
      cancelled = true;
    };
  }, [seatplanDetail]);

  const participantsBySchuelerTableId = useMemo(() => {
    const map = new Map<number, KlassenbuchParticipant>();
    for (const participant of participants) {
      const id = Number(participant.schueler_table_id);
      if (Number.isFinite(id) && id > 0) map.set(id, participant);
    }
    return map;
  }, [participants]);

  const participantsByName = useMemo(() => {
    const map = new Map<string, KlassenbuchParticipant>();
    for (const participant of participants) {
      const key = `${normalizeName(participant.Name || participant.last_name)}|${normalizeName(
        participant.Vorname || participant.first_name
      )}`;
      map.set(key, participant);
    }
    return map;
  }, [participants]);

  const detailByCell = useMemo(() => {
    const map = new Map<string, SeatplanPosition>();
    for (const pos of seatplanDetail?.positionen || []) {
      map.set(`${pos.grid_row}|${pos.grid_col}`, pos);
    }
    return map;
  }, [seatplanDetail]);

  const rows = Math.max(1, Number(seatplanDetail?.grid_rows || 0));
  const cols = Math.max(1, Number(seatplanDetail?.grid_cols || 0));

  const renderList = () => (
    <AttendanceGrid participants={participants} disabled={disabled} onSet={onSet} onOpenExcuse={onOpenExcuse} />
  );

  const renderSeatplan = () => {
    if (!selectedSeatplanId) {
      return (
        <div className="rounded-md border border-dashed border-gray-300 dark:border-gray-700 p-3 text-sm text-gray-500 dark:text-gray-400">
          Keine passende Sitzordnung für diese Klasse/Gruppe gefunden.
        </div>
      );
    }
    if (!seatplanDetail) {
      return (
        <div className="rounded-md border border-dashed border-gray-300 dark:border-gray-700 p-3 text-sm text-gray-500 dark:text-gray-400">
          Sitzordnung wird geladen…
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: rows * cols }, (_, idx) => {
            const r = Math.floor(idx / cols);
            const c = idx % cols;
            const pos = detailByCell.get(`${r}|${c}`) || null;
            if (!pos) {
              return (
                <div
                  key={`${r}-${c}`}
                  className="h-24 rounded-md border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/60"
                />
              );
            }
            const byId = participantsBySchuelerTableId.get(Number(pos.schueler_id)) || null;
            const byName = participantsByName.get(
              `${normalizeName(pos.name)}|${normalizeName(pos.vorname)}`
            ) || null;
            const participant = byId || byName;
            const status = participant?.attendance_status || null;
            const studentKey = participant ? getStudentKey(participant) : null;
            const photoKey = String(pos.sophomorix_unid || '').trim();
            const photoDataUrl = photoKey ? seatplanPhotos[photoKey] || null : null;
            const hasPhoto = !!photoDataUrl;
            const fullName = `${pos.name || ''}, ${pos.vorname || ''}`.trim();
            return (
              <div
                key={`${r}-${c}`}
                className="group relative overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
              >
                <div className="h-24 w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  {hasPhoto ? (
                    <img
                      src={photoDataUrl || ''}
                      alt={fullName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                      {(pos.vorname || '?').slice(0, 1)}
                      {(pos.name || '?').slice(0, 1)}
                    </div>
                  )}
                </div>
                <div className="absolute right-1.5 top-1.5">
                  <span
                    className={`block h-3 w-3 rounded-full ring-2 ring-white/90 dark:ring-gray-900 ${
                      status ? STATUS_DOT_CLASS[status] : 'bg-gray-400'
                    }`}
                    title={
                      status === 'present'
                        ? 'Anwesend'
                        : status === 'late'
                        ? 'Verspätet'
                        : status === 'excused'
                        ? 'Entschuldigt'
                        : status === 'beurlaubt'
                        ? 'Beurlaubt'
                        : status === 'absent'
                        ? 'Fehlt'
                        : 'Noch nicht markiert'
                    }
                  />
                </div>
                <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-1">
                  <div className="grid grid-cols-5 gap-1">
                    {STATUS_ACTIONS.map((action) => {
                      const active = status === action.status;
                      return (
                        <button
                          key={action.status}
                          type="button"
                          disabled={disabled || !studentKey}
                          title={action.title}
                          onClick={() => studentKey && onSet(studentKey, action.status)}
                          className={`h-7 w-7 rounded-md border text-xs flex items-center justify-center ${
                            active
                              ? action.activeClass
                              : 'bg-gray-900/80 border-gray-500 text-gray-100 hover:bg-gray-800'
                          } disabled:opacity-40`}
                        >
                          {action.icon}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="px-1.5 py-1 text-xs leading-tight text-gray-800 dark:text-gray-100 truncate">
                  {fullName}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={mode === 'list' ? 'primary' : 'outline'} onClick={() => setMode('list')}>
          <List className="h-4 w-4" /> Liste
        </Button>
        <Button
          size="sm"
          variant={mode === 'seatplan' ? 'primary' : 'outline'}
          onClick={() => setMode('seatplan')}
          disabled={loadingSeatplans || seatplans.length === 0}
        >
          <LayoutGrid className="h-4 w-4" /> Sitzordnung
        </Button>
        {mode === 'seatplan' && seatplans.length > 1 && (
          <select
            value={selectedSeatplanId || ''}
            onChange={(e) => setSelectedSeatplanId(e.target.value ? Number(e.target.value) : null)}
            className="input-field h-9 min-w-[220px]"
          >
            {seatplans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </select>
        )}
      </div>
      {mode === 'seatplan' ? renderSeatplan() : renderList()}
    </div>
  );
}
